import React, { useState, useEffect, useRef, useCallback } from 'react'
import * as XLSX from 'xlsx'
import ElementsDataService from '../../api/entitiesDataServices/ElementsDataService.js'

const T = {
    bg:'#f1f5f9', card:'#ffffff', border:'#e8edf5',
    blue:'#3b82f6', blueD:'#2563eb', navy:'#0f172a',
    txt:'#1e293b', txt2:'#64748b', muted:'#94a3b8',
    green:'#16a34a', greenBg:'#f0fdf4', greenBdr:'#bbf7d0',
    red:'#dc2626', redBg:'#fef2f2', redBdr:'#fecaca',
    warn:'#f59e0b', warnBg:'#fffbeb', warnBdr:'#fde68a',
    purple:'#7c3aed', purpleBg:'#f5f3ff', purpleBdr:'#ddd6fe',
    cyan:'#0891b2', cyanBg:'#ecfeff', cyanBdr:'#a5f3fc',
    r:16, rsm:10,
    shadow:'0 1px 3px rgba(15,23,42,.06)',
    font:"'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
}

const OBJETOS = ['Contabilidad','Cfdi','Cliente','Domicilio','PersonaFisica','Sistema','Sociedad']

const OBJETO_COLORS = {
    Contabilidad: { bg:'#f0fdf4', border:'#bbf7d0', color:'#16a34a' },
    Cfdi:         { bg:'#eff6ff', border:'#bfdbfe', color:'#2563eb' },
    Cliente:      { bg:'#f5f3ff', border:'#ddd6fe', color:'#7c3aed' },
    Domicilio:    { bg:'#fff7ed', border:'#fed7aa', color:'#ea580c' },
    PersonaFisica:{ bg:'#fdf4ff', border:'#f0abfc', color:'#a21caf' },
    Sistema:      { bg:'#f1f5f9', border:'#cbd5e1', color:'#475569' },
    Sociedad:     { bg:'#ecfeff', border:'#a5f3fc', color:'#0891b2' },
}

function ObjetoBadge({ tipo }) {
    const c = OBJETO_COLORS[tipo] || { bg:T.bg, border:T.border, color:T.txt2 }
    return (
        <span style={{ display:'inline-block', padding:'2px 9px', borderRadius:20, fontSize:11, fontWeight:700,
            background:c.bg, border:`1px solid ${c.border}`, color:c.color, whiteSpace:'nowrap' }}>
            {tipo}
        </span>
    )
}

function extractTipoObjeto(descripcion) {
    if (!descripcion) return null
    for (const obj of OBJETOS) {
        if (descripcion.toLowerCase().includes(obj.toLowerCase())) return obj
    }
    return null
}

/* ─── SimpleSelect con búsqueda ─── */
function SimpleSelect({ label, options, value, onChange, placeholder = 'Todos' }) {
    const [open,  setOpen]  = useState(false)
    const [query, setQuery] = useState('')
    const inputRef     = useRef(null)
    const containerRef = useRef(null)
    useEffect(() => {
        const h = e => { if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false) }
        document.addEventListener('mousedown', h)
        return () => document.removeEventListener('mousedown', h)
    }, [])
    const filtered = options.filter(o => o.toLowerCase().includes(query.toLowerCase()))
    return (
        <div ref={containerRef} style={{ position:'relative', flex:1, minWidth:160 }}>
            <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.txt2,
                marginBottom:4, textTransform:'uppercase', letterSpacing:'.05em' }}>{label}</label>
            <div onClick={() => { setOpen(o => !o); setTimeout(() => inputRef.current?.focus(), 50) }}
                style={{ height:38, padding:'0 12px', borderRadius:T.rsm, border:`1px solid ${open ? T.blue : T.border}`,
                    background:T.card, cursor:'pointer', display:'flex', alignItems:'center',
                    justifyContent:'space-between', gap:8,
                    boxShadow: open ? `0 0 0 3px ${T.blue}20` : T.shadow, transition:'all .15s' }}>
                <span style={{ fontSize:13, color: value ? T.txt : T.muted, fontWeight: value ? 500 : 400 }}>
                    {value || placeholder}
                </span>
                {value && (
                    <span onMouseDown={e => { e.preventDefault(); e.stopPropagation(); onChange('') }}
                        style={{ color:T.muted, fontWeight:700, fontSize:14, lineHeight:1 }}>×</span>
                )}
                <span style={{ color:T.muted, fontSize:11 }}>{open ? '▲' : '▼'}</span>
            </div>
            {open && (
                <div style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:200, marginTop:4,
                    background:T.card, border:`1px solid ${T.border}`, borderRadius:T.rsm,
                    boxShadow:'0 8px 24px rgba(15,23,42,.12)', overflow:'hidden' }}>
                    <div style={{ padding:'6px 10px', borderBottom:`1px solid ${T.border}` }}>
                        <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)}
                            placeholder="Buscar..." onMouseDown={e => e.stopPropagation()}
                            style={{ width:'100%', border:'none', outline:'none', fontSize:12,
                                color:T.txt, fontFamily:T.font, background:'transparent' }} />
                    </div>
                    <div style={{ maxHeight:200, overflowY:'auto' }}>
                        <div onMouseDown={e => { e.preventDefault(); onChange(''); setOpen(false) }}
                            style={{ padding:'8px 12px', fontSize:13, cursor:'pointer', color:T.muted,
                                borderBottom:`1px solid ${T.border}` }}>
                            {placeholder}
                        </div>
                        {filtered.map(o => (
                            <div key={o} onMouseDown={e => { e.preventDefault(); onChange(o); setOpen(false); setQuery('') }}
                                style={{ padding:'8px 12px', fontSize:13, cursor:'pointer',
                                    background: value === o ? `${T.blue}08` : 'transparent',
                                    color: value === o ? T.blue : T.txt,
                                    fontWeight: value === o ? 600 : 400,
                                    borderBottom:`1px solid ${T.border}` }}>
                                {o}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}

const PAGE_SIZE = 50

export default function HistoriaComponent() {
    const [elements,   setElements]   = useState([])
    const [usuarios,   setUsuarios]   = useState([])
    const [count,      setCount]      = useState(0)
    const [loading,    setLoading]    = useState(true)
    const [loadingMore,setLoadingMore] = useState(false)
    const [pageNumber, setPageNumber] = useState(0)
    const [page,       setPage]       = useState(1)
    const PAGE_DISPLAY = 50

    // filtros
    const [fUsuario, setFUsuario] = useState('')
    const [fObjeto,  setFObjeto]  = useState('')
    const [fTexto,   setFTexto]   = useState('')

    // ref para scroll infinito
    const sentinelRef = useRef(null)
    const filtersRef  = useRef({ usuario:'', objeto:'' })

    const buildUrl = useCallback((page, usuario, objeto) =>
        `historia?pageNumber=${page}&size=${PAGE_SIZE}` +
        `&objeto=${objeto || ''}&usuario=${usuario || ''}`
    , [])

    // Carga inicial
    useEffect(() => {
        setLoading(true)
        Promise.all([
            ElementsDataService.retrieveAllElements('historiaCount'),
            ElementsDataService.retrieveAllElements(buildUrl(0, '', '')),
            ElementsDataService.retrieveAllElements('usuarios'),
        ]).then(([cnt, hist, usr]) => {
            setCount(cnt.data || 0)
            setElements(hist.data || [])
            setUsuarios(usr.data || [])
            setPageNumber(0)
        }).finally(() => setLoading(false))
    }, []) // eslint-disable-line

    // Re-fetch cuando cambian filtros
    const applyFilters = useCallback((usuario, objeto) => {
        filtersRef.current = { usuario, objeto }
        setLoading(true)
        setElements([])
        setPageNumber(0)
        setPage(1)
        Promise.all([
            ElementsDataService.retrieveAllElements('historiaCount'),
            ElementsDataService.retrieveAllElements(buildUrl(0, usuario, objeto)),
        ]).then(([cnt, hist]) => {
            setCount(cnt.data || 0)
            setElements(hist.data || [])
        }).finally(() => setLoading(false))
    }, [buildUrl])

    const handleFUsuario = v => { setFUsuario(v); applyFilters(v, fObjeto) }
    const handleFObjeto  = v => { setFObjeto(v);  applyFilters(fUsuario, v) }

    // Scroll infinito con IntersectionObserver
    useEffect(() => {
        const sentinel = sentinelRef.current
        if (!sentinel) return
        const observer = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && !loadingMore && !loading) {
                const nextPage = pageNumber + 1
                if (nextPage * PAGE_SIZE < count) {
                    setLoadingMore(true)
                    const { usuario, objeto } = filtersRef.current
                    ElementsDataService.retrieveAllElements(buildUrl(nextPage, usuario, objeto))
                        .then(r => {
                            setElements(prev => [...prev, ...(r.data || [])])
                            setPageNumber(nextPage)
                        })
                        .finally(() => setLoadingMore(false))
                }
            }
        }, { threshold: 0.1 })
        observer.observe(sentinel)
        return () => observer.disconnect()
    }, [pageNumber, count, loading, loadingMore, buildUrl])

    // Filtro de texto local (sobre los ya cargados)
    const filtered = fTexto
        ? elements.filter(e =>
            e.descripcionEvento?.toLowerCase().includes(fTexto.toLowerCase()) ||
            e.userId?.toLowerCase().includes(fTexto.toLowerCase())
          )
        : elements

    const hasMore = (pageNumber + 1) * PAGE_SIZE < count

    /* ── Excel ── */
    const downloadExcel = () => {
        const data = elements.map((item, i) => ({
            'N°':          i + 1,
            'Usuario':     item.userId || '',
            'Tipo Objeto': item.tipoObjeto || extractTipoObjeto(item.descripcionEvento) || '',
            'Descripción': item.descripcionEvento || '',
            'Fecha/Hora':  new Date(item.dateTime).toLocaleString('es-MX'),
        }))
        const ws = XLSX.utils.json_to_sheet(data)
        ws['!cols'] = [{ wch:5 }, { wch:22 }, { wch:16 }, { wch:80 }, { wch:20 }]
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, 'Historial')
        const tag = fUsuario ? `_${fUsuario}` : fObjeto ? `_${fObjeto}` : ''
        XLSX.writeFile(wb, `Historial${tag}_${new Date().toISOString().slice(0,10)}.xlsx`)
    }

    /* ── estilos tabla ── */
    const thS = { padding:'10px 16px', fontSize:11, fontWeight:700, color:T.txt2, textTransform:'uppercase',
        letterSpacing:'.05em', borderBottom:`2px solid ${T.border}`, background:'#f8fafc', textAlign:'left', whiteSpace:'nowrap' }
    const tdS = { padding:'10px 16px', fontSize:13, color:T.txt, borderBottom:`1px solid ${T.border}`, verticalAlign:'middle' }

    return (
        <div style={{ minHeight:'100vh', background:T.bg, fontFamily:T.font, padding:'24px 28px 64px' }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
                @keyframes spin { to { transform:rotate(360deg) } }
                tr:hover td { background: #eff6ff !important; }
            `}</style>

            <div style={{ maxWidth:1100, margin:'0 auto' }}>

                {/* ── Header ── */}
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, flexWrap:'wrap', gap:12 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:12 }}>
<div>
                            <h1 style={{ margin:0, fontSize:22, fontWeight:800, color:T.navy, letterSpacing:'-0.025em' }}>
                                Historial de Eventos
                            </h1>
                            <p style={{ margin:'3px 0 0', fontSize:13, color:T.txt2 }}>
                                {count.toLocaleString()} eventos totales
                                {(fUsuario || fObjeto) && ` · mostrando ${elements.length.toLocaleString()} con filtros`}
                            </p>
                        </div>
                    </div>

                    {/* Contador + Excel */}
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <span style={{ fontSize:12, fontWeight:600, color:T.blue, background:`${T.blue}12`,
                            border:`1px solid ${T.blue}20`, borderRadius:10, padding:'2px 10px' }}>
                            {elements.length.toLocaleString()} cargados
                        </span>
                        {hasMore && (
                            <span style={{ fontSize:12, color:T.muted }}>de {count.toLocaleString()}</span>
                        )}
                        <button onClick={downloadExcel} disabled={elements.length === 0}
                            style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'7px 14px',
                                borderRadius:T.rsm, border:'none',
                                background: elements.length === 0 ? T.muted : T.blue,
                                color:'white', fontSize:12, fontWeight:700, cursor: elements.length === 0 ? 'not-allowed':'pointer',
                                fontFamily:T.font, boxShadow: elements.length > 0 ? `0 4px 12px ${T.blue}40`:'none' }}>
                            📥 Excel
                        </button>
                    </div>
                </div>

                {/* ── Filtros ── */}
                <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:T.r,
                    boxShadow:T.shadow, padding:'16px 20px', marginBottom:16 }}>
                    <div style={{ display:'flex', gap:12, flexWrap:'wrap', alignItems:'flex-end' }}>
                        <SimpleSelect label="Usuario" options={usuarios} value={fUsuario} onChange={handleFUsuario} />
                        <SimpleSelect label="Tipo de objeto" options={OBJETOS} value={fObjeto} onChange={handleFObjeto} />
                        {/* Búsqueda libre de texto */}
                        <div style={{ flex:2, minWidth:200 }}>
                            <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.txt2,
                                marginBottom:4, textTransform:'uppercase', letterSpacing:'.05em' }}>
                                Buscar en eventos
                            </label>
                            <div style={{ position:'relative' }}>
                                <span style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)',
                                    fontSize:14, color:T.muted, pointerEvents:'none' }}>🔍</span>
                                <input value={fTexto} onChange={e => setFTexto(e.target.value)}
                                    placeholder="Buscar en descripción o usuario..."
                                    style={{ width:'100%', padding:'8px 10px 8px 32px', borderRadius:T.rsm,
                                        border:`1px solid ${T.border}`, fontSize:13, color:T.txt,
                                        fontFamily:T.font, outline:'none', background:T.card,
                                        boxSizing:'border-box' }} />
                                {fTexto && (
                                    <button onClick={() => setFTexto('')}
                                        style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)',
                                            background:'none', border:'none', cursor:'pointer', color:T.muted,
                                            fontSize:16, lineHeight:1, padding:0 }}>×</button>
                                )}
                            </div>
                        </div>
                        {/* Limpiar todos */}
                        {(fUsuario || fObjeto || fTexto) && (
                            <button onClick={() => { setFUsuario(''); setFObjeto(''); setFTexto(''); applyFilters('','') }}
                                style={{ padding:'7px 14px', borderRadius:T.rsm, border:`1px solid ${T.border}`,
                                    background:T.bg, color:T.txt2, fontSize:12, cursor:'pointer',
                                    fontFamily:T.font, whiteSpace:'nowrap', height:38, alignSelf:'flex-end' }}>
                                ✕ Limpiar
                            </button>
                        )}
                    </div>
                    {/* Chips de filtros activos */}
                    {(fUsuario || fObjeto) && (
                        <div style={{ display:'flex', gap:8, marginTop:12, flexWrap:'wrap' }}>
                            {fUsuario && (
                                <span style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'3px 10px',
                                    borderRadius:20, fontSize:12, fontWeight:600, background:`${T.blue}12`,
                                    border:`1px solid ${T.blue}30`, color:T.blueD }}>
                                    👤 {fUsuario}
                                    <span onClick={() => handleFUsuario('')} style={{ cursor:'pointer', fontWeight:800 }}>×</span>
                                </span>
                            )}
                            {fObjeto && (
                                <span style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'3px 10px',
                                    borderRadius:20, fontSize:12, fontWeight:600,
                                    background: OBJETO_COLORS[fObjeto]?.bg || T.bg,
                                    border:`1px solid ${OBJETO_COLORS[fObjeto]?.border || T.border}`,
                                    color: OBJETO_COLORS[fObjeto]?.color || T.txt2 }}>
                                    🏷 {fObjeto}
                                    <span onClick={() => handleFObjeto('')} style={{ cursor:'pointer', fontWeight:800 }}>×</span>
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {/* ── Tabla ── */}
                <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:T.r, boxShadow:T.shadow, overflow:'hidden' }}>
                    <div style={{ padding:'12px 20px', borderBottom:`1px solid ${T.border}`,
                        display:'flex', alignItems:'center', gap:8 }}>
                        <span>📋</span>
                        <h2 style={{ margin:0, fontSize:14, fontWeight:700, color:T.txt }}>Eventos</h2>
                        <span style={{ fontSize:12, fontWeight:700, color:T.blue, background:`${T.blue}12`,
                            border:`1px solid ${T.blue}20`, borderRadius:10, padding:'1px 8px' }}>
                            {fTexto ? filtered.length : elements.length}
                        </span>
                        {loading && (
                            <div style={{ width:16, height:16, border:`2px solid ${T.border}`, borderTopColor:T.blue,
                                borderRadius:'50%', animation:'spin .7s linear infinite', marginLeft:4 }} />
                        )}
                    </div>

                    {loading ? (
                        <div style={{ padding:'60px 24px', textAlign:'center' }}>
                            <div style={{ width:36, height:36, border:`3px solid ${T.border}`, borderTopColor:T.blue,
                                borderRadius:'50%', animation:'spin .7s linear infinite', margin:'0 auto 12px' }} />
                            <p style={{ margin:0, fontSize:14, color:T.muted }}>Cargando historial...</p>
                        </div>
                    ) : filtered.length === 0 ? (
                        <div style={{ padding:'60px 24px', textAlign:'center' }}>
                            <div style={{ fontSize:36, marginBottom:10 }}>🔍</div>
                            <p style={{ margin:0, fontSize:14, fontWeight:600, color:T.txt }}>Sin resultados</p>
                            <p style={{ margin:'4px 0 0', fontSize:13, color:T.muted }}>Ajusta los filtros para ver eventos</p>
                        </div>
                    ) : (
                        <>
                            <table style={{ width:'100%', borderCollapse:'collapse' }}>
                                <thead>
                                    <tr>
                                        <th style={thS}>Usuario</th>
                                        <th style={thS}>Tipo</th>
                                        <th style={{ ...thS, width:'55%' }}>Descripción del Evento</th>
                                        <th style={{ ...thS, whiteSpace:'nowrap' }}>Fecha / Hora</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.slice((page-1)*PAGE_DISPLAY, page*PAGE_DISPLAY).map((item, i) => {
                                        const tipo = item.tipoObjeto || extractTipoObjeto(item.descripcionEvento)
                                        const gi   = (page-1)*PAGE_DISPLAY + i
                                        return (
                                            <tr key={item.id ?? gi}>
                                                <td style={{ ...tdS, background:gi%2===0?T.card:T.bg }}>
                                                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                                                        <div style={{ width:28, height:28, borderRadius:'50%',
                                                            background:`${T.blue}18`, border:`1px solid ${T.blue}30`,
                                                            display:'flex', alignItems:'center', justifyContent:'center',
                                                            fontSize:12, fontWeight:700, color:T.blueD, flexShrink:0 }}>
                                                            {(item.userId || '?')[0].toUpperCase()}
                                                        </div>
                                                        <span style={{ fontSize:13, fontWeight:500, color:T.txt }}>
                                                            {item.userId || '—'}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td style={{ ...tdS, background:gi%2===0?T.card:T.bg }}>
                                                    {tipo ? <ObjetoBadge tipo={tipo} /> : <span style={{ color:T.muted, fontSize:12 }}>—</span>}
                                                </td>
                                                <td style={{ ...tdS, background:gi%2===0?T.card:T.bg, color:T.txt2, lineHeight:1.5 }}>
                                                    {fTexto && item.descripcionEvento
                                                        ? item.descripcionEvento.split(new RegExp(`(${fTexto})`, 'gi')).map((part, pi) =>
                                                            part.toLowerCase() === fTexto.toLowerCase()
                                                                ? <mark key={pi} style={{ background:'#fef08a', borderRadius:3, padding:'0 2px' }}>{part}</mark>
                                                                : part
                                                          )
                                                        : item.descripcionEvento
                                                    }
                                                </td>
                                                <td style={{ ...tdS, background:gi%2===0?T.card:T.bg, fontSize:12,
                                                    color:T.txt2, whiteSpace:'nowrap' }}>
                                                    {new Date(item.dateTime).toLocaleString('es-MX', {
                                                        year:'numeric', month:'short', day:'2-digit',
                                                        hour:'2-digit', minute:'2-digit'
                                                    })}
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>

                            {/* Paginación */}
                            {(() => {
                                const totalPages = Math.ceil(filtered.length / PAGE_DISPLAY)
                                if (totalPages <= 1 && !hasMore) return null
                                return (
                                    <div style={{ padding:'14px 20px', borderTop:`1px solid ${T.border}`,
                                        display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
                                        <span style={{ fontSize:12, color:T.txt2 }}>
                                            Mostrando {((page-1)*PAGE_DISPLAY+1).toLocaleString()}–{Math.min(page*PAGE_DISPLAY, filtered.length).toLocaleString()} de {filtered.length.toLocaleString()}
                                            {hasMore && <span style={{ color:T.warn }}> · hay más por cargar al final</span>}
                                        </span>
                                        <div style={{ display:'flex', gap:4, alignItems:'center' }}>
                                            <button onClick={() => setPage(1)} disabled={page===1}
                                                style={{ padding:'5px 10px', borderRadius:T.rsm, border:`1px solid ${T.border}`,
                                                    background: page===1 ? T.bg : T.card, color: page===1 ? T.muted : T.txt,
                                                    fontSize:12, cursor: page===1 ? 'default':'pointer', fontFamily:T.font }}>«</button>
                                            <button onClick={() => setPage(p => Math.max(1,p-1))} disabled={page===1}
                                                style={{ padding:'5px 10px', borderRadius:T.rsm, border:`1px solid ${T.border}`,
                                                    background: page===1 ? T.bg : T.card, color: page===1 ? T.muted : T.txt,
                                                    fontSize:12, cursor: page===1 ? 'default':'pointer', fontFamily:T.font }}>‹</button>
                                            {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                                                let p
                                                if (totalPages <= 7) p = i + 1
                                                else if (page <= 4) p = i + 1
                                                else if (page >= totalPages - 3) p = totalPages - 6 + i
                                                else p = page - 3 + i
                                                return (
                                                    <button key={p} onClick={() => setPage(p)}
                                                        style={{ padding:'5px 10px', borderRadius:T.rsm, minWidth:32,
                                                            border:`1px solid ${p===page ? T.blue : T.border}`,
                                                            background: p===page ? T.blue : T.card,
                                                            color: p===page ? 'white' : T.txt,
                                                            fontSize:12, cursor:'pointer', fontFamily:T.font, fontWeight: p===page ? 700 : 400 }}>
                                                        {p}
                                                    </button>
                                                )
                                            })}
                                            <button onClick={() => setPage(p => Math.min(totalPages,p+1))} disabled={page===totalPages}
                                                style={{ padding:'5px 10px', borderRadius:T.rsm, border:`1px solid ${T.border}`,
                                                    background: page===totalPages ? T.bg : T.card, color: page===totalPages ? T.muted : T.txt,
                                                    fontSize:12, cursor: page===totalPages ? 'default':'pointer', fontFamily:T.font }}>›</button>
                                            <button onClick={() => setPage(totalPages)} disabled={page===totalPages}
                                                style={{ padding:'5px 10px', borderRadius:T.rsm, border:`1px solid ${T.border}`,
                                                    background: page===totalPages ? T.bg : T.card, color: page===totalPages ? T.muted : T.txt,
                                                    fontSize:12, cursor: page===totalPages ? 'default':'pointer', fontFamily:T.font }}>»</button>
                                        </div>
                                    </div>
                                )
                            })()}

                            {/* Sentinel scroll infinito — carga más páginas del servidor */}
                            <div ref={sentinelRef} style={{ padding:'8px', textAlign:'center' }}>
                                {loadingMore && (
                                    <div style={{ display:'inline-flex', alignItems:'center', gap:8, color:T.txt2, fontSize:12 }}>
                                        <div style={{ width:14, height:14, border:`2px solid ${T.border}`,
                                            borderTopColor:T.blue, borderRadius:'50%',
                                            animation:'spin .7s linear infinite' }} />
                                        Cargando más del servidor...
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>

            </div>
        </div>
    )
}
